<template>
  <Dialog v-model:visible="isModalOpen" @update:visible="handleModalCancel()" modal @closeOnEscape="true"
    header="Set Recurrence">
    <div class="recurrence-div">
      <div class="start-date-div">
        <div class="field-margin">
          <span class="label-span">Start Date</span>
        </div>
        <Calendar v-model="modalStartDate" :max-date="modalEndDate || null" :manualInput="false"
          @date-select="handleStartDateChange" dateFormat="dd-M-yy" />
      </div>

      <div class="interval-div">
        <div class="field-margin">
          <span class="label-span">Interval</span>
        </div>
        <InputNumber v-model="repeatEvery" inputId="minmax-buttons" mode="decimal" showButtons :min="1" :max="99"
          @input="validateModalRecurrence" />
      </div>

      <div class="repeat-every-div">
        <div class="field-margin">
          <span class="label-span">Repeat Every</span>
        </div>
        <Dropdown v-model="frequency" :options="recurrenceModalOptions" optionLabel="name"
          placeholder="Select Occurrence" class="custom-placeholder-color" @change="validateModalRecurrence" />
      </div>
    </div>

    <div v-if="frequency && frequency.id === 'WEEKLY'" class="avatar-div">
      <div v-for="(item, index) in abbreviatedWeekdays" :key="index">
        <Avatar :label="item" class="mr-2"
          :style="{ 'border': `0.156rem solid ${getSelectedColor(index)}`, color: getSelectedColor(index, '#060505') }"
          @click="handleDayOfWeekSelect(index)" />
      </div>
    </div>

    <div v-if="frequency && frequency.id === 'MONTHLY'">
      <div class="flex align-items-center mt-4">
        <RadioButton v-model="monthlyRadioChange" inputId="onday" name="monthlyOnDay" value="onday"
          @change="validateModalRecurrence" />
        <label for="onday" class="ml-2 mr-2">On day</label>
        <InputNumber :disabled="monthlyRadioChange === 'onthe'" v-model="monthOnDay" inputId="minmax-buttons"
          mode="decimal" showButtons :manualInput="false" :min="1" :max="31" @input="validateModalRecurrence" />
      </div>
      <div class="flex align-items-center mt-2">
        <RadioButton v-model="monthlyRadioChange" inputId="onthe" name="monthlyOnThe" value="onthe"
          @change="validateModalRecurrence" />
        <label for="onthe" class="ml-2" @select="validateModalRecurrence">On the</label>
        <Dropdown :disabled="monthlyRadioChange === 'onday'" v-model="monthOnThe" :options="monthIndexOptions"
          optionLabel="name" placeholder="Select Month Index" class="custom-placeholder-color ml-2 mr-2"
          @blur="validateModalRecurrence" />
        <Dropdown :disabled="monthlyRadioChange === 'onday'" v-model="monthWeekRecurrence"
          :options="monthWeekRecurrenceOptions" optionLabel="name" placeholder="Select weekly Occurrence"
          class="custom-placeholder-color ml-2 mr-2" @blur="validateModalRecurrence" />
      </div>
    </div>

    <div class="end-date-div">
      <div class="field-margin">
        <span class="label-span">End Date</span>
      </div>
      <Calendar :min-date="modalStartDate" v-model="modalEndDate" :manualInput="false" @blur="validateModalRecurrence"
        dateFormat="dd-M-yy" :minDate="minDate" />
    </div>
    <div class="ml-1 mt-2">
      {{ setRecurrenceMeetingMessage() }}
    </div>
    <div class="mt-2 button-align">
      <button @click="handleModalSave()" class="primary-filled-button" :disabled="isModalDisabled">
        Save</button>
      <button @click="handleModalCancel()" class="primary-outlined-button">
        Cancel</button>
    </div>
  </Dialog>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, defineProps, onMounted, ref, watch } from 'vue'
import Calendar from 'primevue/calendar'
import Dropdown from 'primevue/dropdown'
import Dialog from 'primevue/dialog'
import InputNumber from 'primevue/inputnumber'
import RadioButton from 'primevue/radiobutton'
import Avatar from 'primevue/avatar'
import { addMinutes } from 'date-fns'
import { RRule } from 'rrule'
import { recurrenceModalOptions, recurrenceOptions } from '@/common/shared.js'
import { formatDate, getISODay, getMonthlyOccurrence } from '@/utils/utils.js'

const props = defineProps({
  isRecurrenceModalOpen: {
    type: Boolean,
    required: true,
  },
  modalValues: {
    type: Object,
    required: true,
  },
  nonModalFrequency: {
    type: Object,
    required: true,
  },
  isResetModal: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['handle-modal-cancel', 'handle-modal-close', 'start-date-change', 'set-state-from-modal'])

const modalStartDate = ref('')
const modalEndDate = ref('')
const minDate = ref('')
const repeatEvery = ref(1)
const frequency = ref('')
const monthlyRadioChange = ref('')
const monthOnDay = ref(1)
const monthOnThe = ref('')
const monthWeekRecurrence = ref('')
const repeatOnList = ref([])
const repeatOn = ref([])
const isModalDisabled = ref(true)
const rrule = ref({})

const isModalOpen = computed(() => props.isRecurrenceModalOpen)
const nonModalFrequency = computed(() => props.nonModalFrequency)
const existingTbData = computed(() => props.modalValues)
const isResetRecurrenceModal = computed(() => props.isResetModal)

const abbreviatedWeekdays = ['M', 'T', 'W', 'Th', 'F', 'Sa', 'S']
const frequencyMapping = {
  'MONTHLY': RRule.MONTHLY,
  'WEEKLY': RRule.WEEKLY,
}
const relativeWeekIndexMap = {
  'First': 1,
  'Second': 2,
  'Third': 3,
  'Fourth': -1,
}
const relativeDayMap = {
  'Monday': RRule.MO,
  'Tuesday': RRule.TU,
  'Wednesday': RRule.WE,
  'Thursday': RRule.TH,
  'Friday': RRule.FR,
  'Saturday': RRule.SA,
  'Sunday': RRule.SU,

}

const parseDate = (dateString) => dateString ? new Date(dateString) : null

const initializeComponentState = () => {
  // Init dates
  const startDefaultValue = formatDate(new Date())
  const endDefaultValue = addMinutes(new Date(startDefaultValue), 60)
  const startDateFromResponse = existingTbData.value?.tb_occurrence?.start_date
  modalStartDate.value = parseDate(startDateFromResponse ? formatDate(startDateFromResponse) : addMinutes(new Date(startDefaultValue), 10))
  const endDateFromResponse = existingTbData.value?.tb_occurrence?.end_date
  modalEndDate.value = parseDate(endDateFromResponse ? formatDate(endDateFromResponse) : endDefaultValue)
  minDate.value = new Date(modalStartDate.value)

  // Init frequency
  const frequencyFromResponse = existingTbData.value?.tb_occurrence?.frequency
  nonModalFrequency.value = frequencyFromResponse ? recurrenceOptions.find((element) => element.id === frequencyFromResponse) : { id: 'DO NOT REPEAT', name: 'Do not Repeat' }
  frequency.value = frequencyFromResponse ? recurrenceModalOptions.find((element) => element.id === frequencyFromResponse) : nonModalFrequency.value

  const repeatOnFromResponse = existingTbData.value?.tb_occurrence?.repeat?.repeat_on
  repeatOnList.value = frequencyFromResponse === 'WEEKLY'
    ? Array.isArray(repeatOnFromResponse)
      ? repeatOnFromResponse
      : [repeatOnFromResponse]
    : []
  repeatOn.value = repeatOnFromResponse || [-1]
  repeatEvery.value = existingTbData.value?.tb_occurrence?.repeat?.repeat_every || 1
  //TODO: Make the values of the radio buttons useful (e.g. 'on the' and 'on day').

  const indexFromResponse = existingTbData.value?.tb_occurrence?.repeat?.index
  monthlyRadioChange.value = indexFromResponse ? 'onthe' : 'onday'
  monthOnDay.value = Array.isArray(repeatOnFromResponse) || (typeof repeatOnFromResponse === 'string') ? 1 : repeatOnFromResponse
  monthOnThe.value = indexFromResponse ? monthIndexOptions[indexFromResponse] : {}
  monthWeekRecurrence.value = indexFromResponse ? monthWeekRecurrenceOptions[repeatOnFromResponse] : {}
}

const resetState = () => {
  modalStartDate.value = formatDate(new Date())
  modalEndDate.value = addMinutes(new Date(modalStartDate.value), 60)
  repeatEvery.value = 1
  frequency.value = ''
  monthlyRadioChange.value = ''
  monthOnDay.value = 1
  monthOnThe.value = ''
  monthWeekRecurrence.value = ''
  repeatOnList.value = []
  repeatOn.value = []
  isModalDisabled.value = true
}

const toIdName = (name) => ({ id: name.toUpperCase(), name })
const monthIndexOptions = ['First', 'Second', 'Third', 'Fourth', 'Fifth'].map(toIdName)
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const monthWeekRecurrenceOptions = daysOfWeek.map(toIdName)

const getStartAndEndDates = () => {
  const frequencyId = frequency.value.id
  const rRuleFrequency = frequencyMapping[frequencyId]
  const startDate = parseDate(modalStartDate.value)
  const endDate = parseDate(modalEndDate.value)

  // Need to create a new Date in UTC per this github issue
  // https://github.com/jkbrzt/rrule/issues/550.
  const startDatetime = new Date(Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    startDate.getUTCDate(),
  ))
  const endDatetime = new Date(Date.UTC(
    endDate.getUTCFullYear(),
    endDate.getUTCMonth(),
    endDate.getUTCDate(),
  ))

  const options = {
    freq: rRuleFrequency,
    interval: repeatEvery.value,
    dtstart: startDatetime,
    until: endDatetime,
  }

  if (rRuleFrequency === RRule.WEEKLY) {
    // Weekly
    options.byweekday = repeatOn.value
  } else if (rRuleFrequency === RRule.MONTHLY) {
    if (monthlyRadioChange.value === 'onthe' && monthOnThe.value) {
      // Monthly on relative day of month i.e. third thursday of every month
      const relativeWeekIndex = relativeWeekIndexMap[monthOnThe.value.name]
      const relativeDay = relativeDayMap[monthWeekRecurrence.value.name]
      options.byweekday = relativeDay?.nth(relativeWeekIndex)
    } else if (monthlyRadioChange.value === 'onday' && monthOnDay.value) {
      // Monthly on specific day of month i.e. 15th of every month
      options.bymonthday = monthOnDay.value
    }
  }

  const rule = new RRule(options)

  // The following code fixes the incorrect dates returned by RRule.js, correcting them to the right recurrence dates.
  // https://github.com/jkbrzt/rrule/issues/550
  const occurrences = rule.all().map(date => {
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
    )
  })
  const start = occurrences[0]
  const end = occurrences.at(-1)
  return { start, end }
}

const validateModalRecurrence = () => {
  const requiredFieldsEntered = !frequency.value || !modalStartDate.value || !modalEndDate.value || !repeatEvery.value
  const endDateIsAfterStartDate = modalEndDate.value && new Date(modalEndDate.value) < new Date(modalStartDate.value)
  const isWeeklyAndNoDaysSelected = frequency.value?.id === 'WEEKLY' && repeatOnList.value.length === 0
  const isMonthlyAndNoDaysSelected = frequency.value?.id === 'MONTHLY' && !monthOnDay.value && !monthOnThe.value?.id
  const noRecurrenceMessage = setRecurrenceMeetingMessage() === ''
  const recurrenceInputsInvalid = requiredFieldsEntered || endDateIsAfterStartDate || isWeeklyAndNoDaysSelected || isMonthlyAndNoDaysSelected || noRecurrenceMessage

  if (!recurrenceInputsInvalid && frequency.value.id !== 'DAILY') {
    rrule.value = getStartAndEndDates()
    if (
      rrule.value?.start &&
      rrule.value?.end &&
      rrule.value?.start !== modalStartDate.value &&
      rrule.value?.end !== modalEndDate.value
    ) {
      modalStartDate.value = rrule.value.start
      modalEndDate.value = rrule.value.end
    }
  }

  isModalDisabled.value = recurrenceInputsInvalid || (frequency.value.id !== 'DAILY' && (!rrule.value.start || !rrule.value.end))
}

const handleModalCancel = (event) => {
  initializeComponentState()
  emit('handle-modal-cancel', event)
}

const getModifiedModalState = () => {
  const modifiedModalState = {
    modalStartDate: modalStartDate.value,
    frequency: frequency.value,
    repeatEvery: repeatEvery.value,
    ...(repeatOn.value && { repeatOn: repeatOn.value }),
    ...(monthWeekRecurrence.value && { monthWeekRecurrence: monthWeekRecurrence.value }),
    ...(monthOnDay.value && { monthOnDay: monthOnDay.value }),
    ...(monthOnThe.value && { monthOnThe: monthOnThe.value }),
    ...(monthlyRadioChange.value && { monthlyRadioChange: monthlyRadioChange.value }),
    ...(modalEndDate.value && { modalEndDate: modalEndDate.value }),
    recurrenceMessage: setRecurrenceMeetingMessage(),
  }

  return modifiedModalState
}

const handleModalSave = () => {
  const modifiedState = getModifiedModalState()
  isResetRecurrenceModal.value = false
  emit('set-state-from-modal', modifiedState)
  emit('handle-modal-close', isModalOpen.value)
}

const getSelectedColor = (index, color) => {
  const isWeekPresent = repeatOnList.value.some((element) => element === index)
  const textColor = color || '#D1CBD2'
  return isWeekPresent ? '#3B82F6' : textColor
}

const dayOfWeekAsString = (dayIndex) => daysOfWeek[dayIndex] || ''

const addWeekdayToRepeat = (itemIndex) => {
  if (repeatOnList?.value.includes(itemIndex)) {
    repeatOnList.value = repeatOnList.value.filter((day) => day !== itemIndex)
  } else {
    repeatOnList.value = [...repeatOnList.value, itemIndex]
  }

  repeatOn.value = repeatOnList.value?.length ? repeatOnList.value : [-1]
}

const handleStartDateChange = () => {
  const startDate = parseDate(modalStartDate.value)
  const dayOfWeekIndex = getISODay(startDate)
  if (frequency.value?.id === 'WEEKLY') {
    repeatOnList.value = []
    addWeekdayToRepeat(dayOfWeekIndex)
  }
  if (frequency.value?.id === 'MONTHLY' && monthlyRadioChange.value === 'onthe') {
    const newDay = daysOfWeek[dayOfWeekIndex]
    const occurrence = getMonthlyOccurrence(startDate)
    monthOnThe.value = monthIndexOptions[occurrence - 1]
    monthWeekRecurrence.value = {
      id: newDay.toUpperCase(),
      name: newDay,
    }
  }

  if (frequency.value?.id === 'MONTHLY' && monthlyRadioChange.value === 'onday') {
    const selectedDate = startDate.getDate()
    monthOnDay.value = selectedDate
  }
  validateModalRecurrence()
}

const handleDayOfWeekSelect = (index) => {
  addWeekdayToRepeat(index)
  validateModalRecurrence()
}

const setRecurrenceMeetingMessage = () => {
  let recurrenceMessage = ''
  const endDate = modalEndDate.value || ''
  const endValue =
    endDate !== '' ? ` until ${formatDate(endDate)}` : ''
  if (frequency.value?.id === 'DAILY') {
    const occurrenceValue = repeatEvery.value
      ? repeatEvery.value > 1
        ? `${repeatEvery.value} days`
        : 'day'
      : ''
    if (occurrenceValue !== '') {
      recurrenceMessage = `Occurs every ${occurrenceValue} starting ${formatDate(modalStartDate.value)}`
    }
  }
  if (frequency.value?.id === 'WEEKLY') {
    const recurringWeeks =
      repeatOnList.value &&
      repeatOnList.value?.map((element) => dayOfWeekAsString(element))
    const occurrenceValue = repeatEvery.value > 1 ? `${repeatEvery.value} weeks` : ''
    const weekOccurrenceValue = occurrenceValue ? `${occurrenceValue} on` : ''
    if (repeatEvery.value && recurringWeeks?.length > 0 && recurringWeeks.length < 7) {
      recurrenceMessage = `Occurs every ${weekOccurrenceValue} ${recurringWeeks.toString()} from ${formatDate(modalStartDate.value)}`
    }
    if (repeatEvery.value && recurringWeeks?.length === 7) {
      recurrenceMessage = `Occurs every day starting ${formatDate(modalStartDate.value)}`
    }
  }
  if (frequency.value?.id === 'MONTHLY') {
    // TODO: refactor `DAILY`, `WEEKLY`, and `MONTHLY` code using helper functions to make this easier to maintain.
    const occurrenceValue = repeatEvery.value ? repeatEvery.value > 1 ? `${repeatEvery.value} months` : 'month' : ''
    if (occurrenceValue && monthlyRadioChange.value === 'onday' && monthOnDay.value) {
      recurrenceMessage = `Occurs every ${occurrenceValue} on day ${monthOnDay.value} starting ${formatDate(modalStartDate.value)}`
    } else if (monthlyRadioChange.value !== 'onday' && monthOnThe.value?.name && monthWeekRecurrence.value?.name) {
      recurrenceMessage = `Occurs every ${occurrenceValue} on ${monthOnThe.value?.name} ${monthWeekRecurrence.value?.name} starting ${formatDate(modalStartDate.value)}`
    }
  }
  return recurrenceMessage ? recurrenceMessage + endValue : ''
}

// Keep the Calendar component in this component in sync with the Calendar
// component in the parent component.
watch(existingTbData, (newValues) => {
  modalStartDate.value = newValues?.tb_occurrence?.start_date ? formatDate(newValues.tb_occurrence.start_date) : modalStartDate.value
})

watch(modalStartDate, (newStartValue) => {
  if (!newStartValue) { return }
  emit('start-date-change', newStartValue)
})

watch(isResetRecurrenceModal, (newResetValue) => {
  if (!newResetValue) { return }
  isResetRecurrenceModal.value = newResetValue
  resetState()
})

watch(nonModalFrequency, (newFrequency) => {
  if (!newFrequency) { return }
  frequency.value = recurrenceModalOptions.find((element) => element.id === newFrequency.id)
})

onMounted(() => {
  initializeComponentState()

  const recurrenceMessage = setRecurrenceMeetingMessage()
  if (recurrenceMessage) {
    const modifiedModalState = getModifiedModalState()
    emit('set-state-from-modal', modifiedModalState)
  }
})

</script>

<style lang="scss" scoped>
.label-span {
  color: var(--Grey600);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.125rem;
}

.field-margin {
  margin-top: 2%;
  margin-bottom: 1%;
}

.recurrence-div {
  display: flex;
  width: 34.375rem !important;
}

.interval-div {
  margin-left: 4%;
  margin-right: 4%;
  margin-top: -0.5%
}

.end-date-div {
  margin-top: 2%;
  width: 28%;
}

.avatar-div {
  display: flex;
  margin-top: 2%;
  cursor: pointer;
}

.primary-filled-button {
  margin-left: 0.5%;
}

.button-align {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
}

.ml-1 {
  max-width: 31.25rem;
  word-wrap: break-word;
}

.mt-2 {
  min-height: 2.5rem;
}
</style>
